import { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../../context/LanguageContext';

const NAV_ITEMS = [
    { key: 'experience', id: 'experience' },
    { key: 'projects', id: 'projects' },
    { key: 'skills', id: 'skills' },
    { key: 'education', id: 'education' },
    { key: 'awards', id: 'awards' },
];

export function NavBar() {
    const { lang, toggleLang, t } = useLanguage();
    const [isDark, setIsDark] = useState(false);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [activeSection, setActiveSection] = useState('');

    useEffect(() => {
        const isDarkStored = localStorage.getItem('portfolio-dark') === 'true' ||
            (!localStorage.getItem('portfolio-dark') && window.matchMedia('(prefers-color-scheme: dark)').matches);
        setIsDark(isDarkStored);
        document.documentElement.classList.toggle('dark', isDarkStored);
    }, []);

    const toggleDark = useCallback(() => {
        setIsDark(prev => {
            const next = !prev;
            localStorage.setItem('portfolio-dark', String(next));
            document.documentElement.classList.toggle('dark', next);
            return next;
        });
    }, []);

    useEffect(() => {
        const observer = new IntersectionObserver(
            entries => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        setActiveSection(entry.target.id);
                    }
                });
            },
            { rootMargin: '-20% 0px -70% 0px' }
        );

        NAV_ITEMS.forEach(item => {
            const el = document.getElementById(item.id);
            if (el) observer.observe(el);
        });

        return () => observer.disconnect();
    }, []);

    const scrollTo = (id: string) => {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
        setIsMenuOpen(false);
    };

    return (
        <nav className="fixed top-0 w-full z-50 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md border-b border-slate-200 dark:border-slate-700">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
                <button onClick={() => scrollTo('hero')} className="text-lg font-bold text-blue-600 dark:text-blue-400">
                    PS
                </button>

                <div className="hidden md:flex items-center gap-6">
                    {NAV_ITEMS.map(item => (
                        <button
                            key={item.id}
                            onClick={() => scrollTo(item.id)}
                            className={`text-sm font-medium transition-colors ${activeSection === item.id
                                ? 'text-blue-600 dark:text-blue-400'
                                : 'text-slate-700 dark:text-slate-300 hover:text-blue-600 dark:hover:text-blue-400'
                                }`}
                        >
                            {t(`nav.${item.key}`)}
                        </button>
                    ))}
                </div>

                <div className="flex items-center gap-3">
                    <button
                        onClick={toggleLang}
                        className="text-sm font-medium text-slate-600 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors px-2 py-1 rounded"
                    >
                        {lang === 'en' ? 'ES' : 'EN'}
                    </button>

                    <button
                        onClick={toggleDark}
                        className="text-slate-600 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors p-2"
                        aria-label="Toggle dark mode"
                    >
                        <i className={`bi ${isDark ? 'bi-sun' : 'bi-moon-stars'} text-lg`} />
                    </button>

                    <button
                        onClick={() => setIsMenuOpen(!isMenuOpen)}
                        className="md:hidden text-slate-600 dark:text-slate-400 p-2"
                        aria-label="Toggle menu"
                    >
                        <i className={`bi ${isMenuOpen ? 'bi-x-lg' : 'bi-list'} text-xl`} />
                    </button>
                </div>
            </div>

            {isMenuOpen && (
                <div className="md:hidden bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-700 px-4 py-4 space-y-3">
                    {NAV_ITEMS.map(item => (
                        <button
                            key={item.id}
                            onClick={() => scrollTo(item.id)}
                            className="block w-full text-left text-sm font-medium text-slate-700 dark:text-slate-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors py-2"
                        >
                            {t(`nav.${item.key}`)}
                        </button>
                    ))}
                </div>
            )}
        </nav>
    );
}
